/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { CreditCard, Trash2, Users } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../services/apiService";
import { Badge } from "../../../components/ops/Badge";
import { CollapsibleRow } from "../../../components/ops/CollapsibleRow";
import { useConfirm } from "../../../components/ops/ConfirmProvider";
import { DataToolbar } from "../../../components/ops/DataToolbar";
import { EmptyState } from "../../../components/ops/EmptyState";
import { PageHeader } from "../../../components/ops/PageHeader";
import { Pagination } from "../../../components/ops/Pagination";
import { useToast } from "../../../components/ops/ToastProvider";
import { usePagedList } from "../../../components/ops/usePagedList";

const statusTone = { active: "success", pending: "neutral", past_due: "warning", paused: "neutral", cancelled: "danger" };
const enrollmentStatuses = ["pending", "active", "past_due", "paused", "cancelled"];

export function StaffEnrollmentsPage() {
  const { accessToken } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/memberships/enrollments")
      .then((response) => setEnrollments(response.data.enrollments || []))
      .catch(() => toast.error("Unable to load enrollments"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [accessToken]);

  const filtered = useMemo(() => {
    return enrollments.filter((enrollment) => {
      if (statusFilter !== "all" && enrollment.status !== statusFilter) return false;
      return true;
    });
  }, [enrollments, statusFilter]);

  const { page, setPage, totalPages, totalItems, pageItems } = usePagedList(filtered, {
    search,
    searchFields: ["player.firstName", "player.lastName", "parent.firstName", "parent.lastName", "parent.email", "plan.name"],
    pageSize: 10,
  });

  const changeStatus = async (enrollment, status) => {
    try {
      const response = await api.patch(`/memberships/enrollments/${enrollment._id}/status`, { status });
      setEnrollments(enrollments.map((e) => (e._id === response.data.enrollment._id ? response.data.enrollment : e)));
      toast.success("Enrollment updated.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to update enrollment");
    }
  };

  const removeEnrollment = async (enrollment) => {
    const ok = await confirm({
      title: "Delete this enrollment?",
      message: `The enrollment for "${enrollment.player?.firstName || "this player"}" will be permanently removed. This can't be undone.`,
      confirmLabel: "Delete enrollment",
      tone: "danger",
    });
    if (!ok) return;

    try {
      await api.delete(`/memberships/enrollments/${enrollment._id}`);
      setEnrollments(enrollments.filter((e) => e._id !== enrollment._id));
      toast.success("Enrollment deleted.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to delete enrollment");
    }
  };

  return (
    <div>
      <PageHeader title="Enrollments" subtitle="Manage family membership enrollments and status" />

      <div className="ops-card">
        <h2 className="ops-card-title">All Enrollments</h2>
        <DataToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by player or parent..."
          filters={[
            {
              label: "Status",
              value: statusFilter,
              onChange: setStatusFilter,
              options: [{ value: "all", label: "All statuses" }, ...enrollmentStatuses.map((status) => ({ value: status, label: status.replace("_", " ") }))],
            },
          ]}
          resultCount={totalItems}
          itemLabel="enrollments"
        />
        {loading ? (
          <p className="ops-loading">Loading enrollments...</p>
        ) : totalItems === 0 ? (
          <EmptyState icon={Users} title="No enrollments found" description={enrollments.length === 0 ? "Enrollments appear here once families complete checkout." : "Try adjusting your search or filters."} />
        ) : (
          <>
            <ul className="ops-list">
              {pageItems.map((enrollment) => (
                <CollapsibleRow
                  key={enrollment._id}
                  avatar={<CreditCard size={16} />}
                  title={`${enrollment.player?.firstName || ""} ${enrollment.player?.lastName || ""} — ${enrollment.plan?.name || "Plan"}`}
                  meta={`${enrollment.parent?.firstName || ""} ${enrollment.parent?.lastName || ""} (${enrollment.parent?.email || "—"})`}
                  trailing={
                    <>
                      <Badge tone={statusTone[enrollment.status] || "neutral"}>{enrollment.status.replace("_", " ")}</Badge>
                      <select
                        className="ops-select"
                        style={{ width: "auto", padding: "6px 10px" }}
                        value={enrollment.status}
                        onChange={(e) => changeStatus(enrollment, e.target.value)}
                      >
                        {enrollmentStatuses.map((status) => <option key={status} value={status}>{status.replace("_", " ")}</option>)}
                      </select>
                      <button type="button" className="ops-button ops-button--danger ops-button--small" onClick={() => removeEnrollment(enrollment)} aria-label="Delete enrollment">
                        <Trash2 size={13} />
                      </button>
                    </>
                  }
                >
                  <p><strong>Plan price:</strong> ${((enrollment.plan?.priceCents || 0) / 100).toFixed(2)} / {enrollment.plan?.billingInterval || "month"}</p>
                  <p><strong>Enrolled:</strong> {new Date(enrollment.createdAt).toLocaleDateString()}</p>
                </CollapsibleRow>
              ))}
            </ul>
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}
